import Vue from 'vue'
import Vuex from 'vuex'
import AuthService from "./services/AuthService";
import CombinedFilters from '@/filters.js'

import _ from 'lodash'
import RealmService from "./services/RealmService";

Vue.use(Vuex)

/**
 * @typedef {Object} UserIdentity
 * @property {number} status
 * @property {string} username
 */

/**
 * @typedef {Object} NodeInfo
 * @property {number} nSelectionInterviews
 * @property {number} nTrendInterviews
 */

export default new Vuex.Store({
  state: {
    /** @type UserIdentity */
    userIdentity: null,

    realmStructure: null,

    reports: [],

    activeStudy: null,

    activeView: null,

    // path minus survey and filtering
    absoluteDrillPath: null,

    /** @type NodeInfo */
    nodeInfo: null,

    selectedDriver: null,

    chartRedrawCounter: 0,

    isFilterBarExpanded: false
  },

  getters: {
    isLoggedIn: state => {
      return (state.userIdentity !== null && state.userIdentity.status===0)
    },

    didLoginFail: state => {
      return (state.userIdentity !== null && state.userIdentity.status===1)
    },

    isInSelectRealmState: state => {
      return (state.userIdentity !== null && state.userIdentity.status===2)
    },

    authToken: state => {
      return (state.userIdentity !=null) ? state.userIdentity.authToken : "?authtoken";
    },

    realmName: state => {
      return (state.userIdentity !=null) ? state.userIdentity.realmName :"?realm"
    },

    realmTitle: state => {
      return (state.realmStructure !=null) ? state.realmStructure.title : "?realm"
    },


    studyName: state => {
      return (state.activeStudy!==null) ? state.activeStudy.extname :"?study"
    },

    absoluteDrillPath: state => {
      return state.absoluteDrillPath
    },

    pageByName: (state) => (extname) => {
      return _.find(state.activeStudy.pages, { 'extname': extname} )
    },

    pages: state => {
      return (state.activeStudy != null) ? state.activeStudy.pages : []
    },

    showKeyIngressMenu: state => {
      return (state.activeStudy != null) ? state.activeStudy.keyIngressPage : false
    },

    reports: state => {
      return (state.activeStudy != null) ? state.activeStudy.reports : []
    },

    studies: state => {
      return (state.realmStructure && state.realmStructure.studies) ?  state.realmStructure.studies : []
    },

    views: state => {
      return (state.activeStudy!==null) ? state.activeStudy.views : []
    },

    completePath: state => {
      if(state.activeStudy!=null && state.activeView!=null && state.absoluteDrillPath!=null) {
        let p = state.activeStudy.extname + "/" + state.activeView.extname

        if(state.absoluteDrillPath && state.absoluteDrillPath.length>0) {
          p += '/' + state.absoluteDrillPath
        }

        let filterTokens = state.activeStudy.combinedFilters.asTokenString()
        if(filterTokens && filterTokens.length>0) {
          p += ':' + filterTokens
        }

        return p;
//        return state.activeStudy.extname + "/" + state.activeView.extname + "/" + state.absoluteDrillPath; // + ':' + state.activeStudy.combinedFilters.asTokenString()
      }

      return "/"
    },

    getIdleTimeout: state => {
      return (state.userIdentity) ? state.userIdentity.idleTimeout : 0
    },

    getLocale: state => {
      return (state.userIdentity) && (state.userIdentity.locale) ? state.userIdentity.locale : "de_DE"
    },

    getTranslation: (state) => (key) => {
      if(state.activeStudy != null) {
        return state.activeStudy.texts[key] ? state.activeStudy.texts[key] : "?"+key;
      }

      // if(!state.realmStructure != null) {
      //   return state.realmStructure.texts[key] ? state.realmStructure.texts[key] : "?"+key;
      // }
      return ""
    },

    combinedFilters: state => {
      if(state.activeStudy!==null) {
        return state.activeStudy.combinedFilters
      }
      // console.log("getter combinedFilters called but not active study")
    },

    isVarFiltersSelected: state => {
      return state.activeStudy.combinedFilters.getNumberOfSelectedFilters()>0
    },

    // filterdefs
    selectionTimeFilterDef: state => {
      if(state.activeStudy!=null) {
        return state.activeStudy.standardFilterSet.selectionTimeFilter;
      }

      return "undef"
    },

    // filterdefs
    trendTimeFilterDef: state => {
      if(state.activeStudy!=null) {
        return state.activeStudy.standardFilterSet.trendTimeFilter;
      }

      return "undef"
    },

    // filterdefs
    variableFilterDefs: state => {
      if(state.activeStudy!=null) {
        return state.activeStudy.standardFilterSet.filterDefs;
      }

      return "undef"
    },


    getFilterDef: (state) => (name) => {
      // console.log("getFilterDef: " + name)
      if(!state.activeStudy != null) {
        return _.find(state.activeStudy.standardFilterSet.filterDefs, { extName: name })
      }
      return "undef"
    },

    getSelectedFilterOptions: (state) => (filterName) => {
      if(!state.activeStudy != null) {
        return state.activeStudy.combinedFilters.getSelectedFilterOptions(filterName);
      }

      return []
    },

    getLogoURL: state => {
      if(!state.realmStructure != null) {
        return state.realmStructure.logoURL
      }
      return ""
    },

  },

  mutations: {
    login (state, userIdentify) {
      state.userIdentity = userIdentify
    },

    logout (state) {
      state.userIdentity = null
      state.realmStructure = null
      state.activeStudy=null
      state.absoluteDrillPath=null
      state.combinedFilters=null
      state.reports = []
      state.isFilterBarExpanded = false
    },

    realmStructure (state, realmStructureDTO) {
      state.realmStructure = realmStructureDTO
    },

    selectStudy (state, studyName) {
      state.activeStudy = _.find(state.realmStructure.studies, { 'extname': studyName})

      if(state.activeStudy!==null) {
        state.activeView = _.find(state.activeStudy.views, { 'extname': state.activeStudy.defaultView})

        if(state.activeView!==null) {
          state.absoluteDrillPath = state.activeView.defaultPath
        }

        if(!state.activeStudy.combinedFilters) {
          Vue.set(state.activeStudy, "combinedFilters", new CombinedFilters( )  )
          state.activeStudy.combinedFilters.setFiltersFromTokenString(state.activeStudy.standardFilterSet, state.activeStudy.defaultFilter)
        }

        state.reports = []
      }

    },

    selectView (state, viewName) {
      state.activeView = _.find(state.activeStudy.views, { 'extname': viewName})
      state.absoluteDrillPath = state.activeView.defaultPath
    },


    selectDriver (state, driverName) {
      state.selectedDriver = driverName
    },

    // drillvalue1/drillvalue2:z=2019;vz=2018
    setCompleteDrillPath(state, completePath) {
//      console.log("Set complete drillpath: " + completePath)

      let drillAndFilterParts = completePath.split(':')

      if (drillAndFilterParts.length == 2) {
        state.absoluteDrillPath = drillAndFilterParts[0]
        state.activeStudy.combinedFilters.setFiltersFromTokenString(state.activeStudy.standardFilterSet, drillAndFilterParts[1])
      } else if(completePath==='')  {
//        console.log("EMPTY:"+completePath)
        state.absoluteDrillPath = ""
        state.activeStudy.combinedFilters.setFiltersFromTokenString(state.activeStudy.standardFilterSet, "")

      } else {
//        console.log("UUUUHHH: '"+completePath + "'")
      }

    },

    setAbsoluteDrillPath(state, path) {
      state.absoluteDrillPath = path;
//      console.log("Set AbsoluteDrillPath to:" + path)
    },

    setNodeInfo(state, nodeInfo) {
      state.nodeInfo = nodeInfo;
    },

    setFilterOptions (state, { filterDef, filterOptions }) {
      state.activeStudy.combinedFilters.setFilterOptions(filterDef.extName, filterOptions)
    },

    addReport (state, reportUUID) {
      state.reports.push(reportUUID)
    },

    increaseChartRedrawCounter (state) {
      state.chartRedrawCounter++
    },

    toggleFilterBarExpanded (state) {
      state.isFilterBarExpanded = !state.isFilterBarExpanded
    }

  },

  actions: {
    selectStudyAndPath({commit, state}, studyName) {
      commit('selectStudy', studyName)
      commit('setCompleteDrillPath', state.activeView.defaultPath)
    },

    authenticateUserPassword(context, {username, password, remember, realm}) {
      AuthService.authenticateUserPassword(username, password, remember, realm).then((response) => {
//        console.log(response.data)
        context.commit('login', response.data)

        // if(remember) {
        //   this._vm.$cookies.set("sessionId", response.data.authToken )
        //   console.log("Created cookie=" + response.data.authToken)
        // }

      }).catch((error) => {
        console.warn('Nicht gut :(' + error);
      })

    },


    actAsUser: ({commit, state}, userId) => {
      let currentRealmName = state.userIdentity.realmName
      let currentStudyName = state.activeStudy.extname
      let authToken = state.userIdentity.authToken

      return AuthService.actAsUser(authToken, currentRealmName, currentStudyName, userId).then((response) => {
        let newUserIdentity = response.data

        return RealmService.fetchStructure(authToken, currentRealmName).then((response) => {
          const structure = response.data

          commit('logout')
          commit('login', newUserIdentity)
          commit('realmStructure', structure)
          commit('selectStudy', structure.defaultStudyName)
        })
      })
    },




  }

})
